import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Network, 
  FileText, 
  Users, 
  Settings,
  TrendingUp,
  LogOut,
  ArrowDownToLine,
  RefreshCw,
  GitCompareArrows,
  Scale,
  X,
  AlertTriangle,
  CloudOff,
  List,
  Shield,
  BookOpen,
  ClipboardCheck,
  UserX,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Networks', href: '/networks', icon: Network },
  { name: 'AC Compare', href: '/ac-compare', icon: GitCompareArrows },
  { name: 'Raketech', href: '/raketech', icon: Scale, children: [
    { name: 'Per Account', href: '/raketech/accounts', icon: List },
    { name: 'Commission Errors', href: '/raketech/underpaid', icon: AlertTriangle },
    { name: 'Unsynced Accounts', href: '/raketech/unsynced', icon: CloudOff },
    { name: 'Final Recon', href: '/raketech/final-recon', icon: ClipboardCheck },
    { name: 'Report', href: '/raketech/report', icon: FileText },
    { name: 'Final Report', href: '/raketech/final-report', icon: FileText },
    { name: 'Final Report v2', href: '/raketech/final-report-v2', icon: FileText },
    { name: 'Final Report v3', href: '/raketech/final-report-v3', icon: FileText },
    { name: 'Final Report v4', href: '/raketech/final-report-v4', icon: FileText },
    { name: 'Post-Recon AC', href: '/raketech/post-recon', icon: Shield },
    { name: 'Missing Accounts', href: '/raketech/missing-accounts', icon: UserX },
    { name: 'Old vs New Recon', href: '/raketech/recon-diff', icon: GitCompareArrows },
    { name: 'Documentation', href: '/raketech/docs', icon: BookOpen },
  ]},
  { name: 'Sync All', href: '/sync-all', icon: RefreshCw },
  { name: 'Import', href: '/import', icon: ArrowDownToLine },
  { name: 'Reports', href: '/reports', icon: FileText },
  { name: 'Players', href: '/players', icon: Users },
];

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  return (
    <div className="flex h-screen w-64 flex-col bg-[hsl(225,15%,8%)] border-r border-[hsl(225,15%,15%)]">
      {/* Logo/Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-[hsl(225,15%,15%)] px-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/20">
          <TrendingUp className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-white tracking-tight">Adwstats</h1>
          <p className="text-[10px] uppercase tracking-widest text-emerald-400/80">iGaming Tracking</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-[hsl(225,15%,12%)] transition-colors lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navigation.map((item) => {
          const isParentActive = location.pathname === item.href || (item.children && location.pathname.startsWith(item.href + '/'));
          const showChildren = item.children && location.pathname.startsWith(item.href);

          return (
            <div key={item.name}>
              <NavLink
                to={item.href}
                end={!!item.children}
                className={({ isActive }) => {
                  const active = isActive || isParentActive;
                  return `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'bg-emerald-500/10 text-emerald-400 shadow-sm'
                      : 'text-zinc-400 hover:bg-[hsl(225,15%,12%)] hover:text-white'
                  }`;
                }}
              >
                {({ isActive }) => {
                  const active = isActive || isParentActive;
                  return (
                    <>
                      <item.icon className={`h-5 w-5 transition-colors ${active ? 'text-emerald-400' : 'text-zinc-500 group-hover:text-zinc-300'}`} />
                      {item.name}
                      {active && (
                        <div className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      )}
                    </>
                  );
                }}
              </NavLink>
              {showChildren && item.children && (
                <div className="ml-6 mt-1 space-y-0.5 border-l border-[hsl(225,15%,18%)] pl-3">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      className={({ isActive }) =>
                        `group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'text-zinc-500 hover:bg-[hsl(225,15%,12%)] hover:text-zinc-300'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <child.icon className={`h-3.5 w-3.5 transition-colors ${isActive ? 'text-emerald-400' : 'text-zinc-600 group-hover:text-zinc-400'}`} />
                          {child.name}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Settings at bottom */}
      <div className="border-t border-[hsl(225,15%,15%)] p-3">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              isActive
                ? 'bg-emerald-500/10 text-emerald-400'
                : 'text-zinc-400 hover:bg-[hsl(225,15%,12%)] hover:text-white'
            }`
          }
        >
          <Settings className="h-5 w-5 text-zinc-500 group-hover:text-zinc-300" />
          Settings
        </NavLink>
      </div>

      {/* User info */}
      <div className="border-t border-[hsl(225,15%,15%)] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 text-sm font-semibold text-emerald-400 ring-1 ring-emerald-500/30">
            {user?.full_name ? getInitials(user.full_name) : 'U'}
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="truncate text-sm font-medium text-white">{user?.full_name || 'User'}</p>
            <p className="truncate text-xs text-zinc-500">{user?.email || ''}</p>
          </div>
          <button 
            onClick={() => logout()}
            className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
